import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, MessageSquare, Phone, Loader2 } from 'lucide-react';
import dayjs from 'dayjs';
import { api } from '../apiClient';

const STATUS_STYLES = {
  pending: { bg: 'bg-amber-50', text: 'text-amber-600', label: 'PENDING' },
  contacted: { bg: 'bg-pink-50', text: 'text-pink-700', label: 'CONTACTED' },
  closed: { bg: 'bg-emerald-50', text: 'text-emerald-600', label: 'CLOSED' },
};

export default function MyEnquiriesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/enquiries/mine');
      setItems(r.data);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  return (
    <div className="max-w-3xl mx-auto px-6" data-testid="my-enquiries-page">
      <div className="flex items-center justify-between py-4">
        <button data-testid="enquiries-back-btn" onClick={() => navigate(-1)} className="p-1 rounded-lg hover:bg-gray-100 transition-colors">
          <ChevronLeft className="w-6 h-6 text-gray-900" />
        </button>
        <h1 className="text-lg font-black text-gray-900">My Enquiries</h1>
        <div className="w-6" />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-magenta" /></div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20" data-testid="enquiries-empty">
          <MessageSquare className="w-16 h-16 text-gray-300 mb-4" />
          <h3 className="text-lg font-extrabold text-gray-900">No enquiries yet</h3>
          <p className="text-sm text-gray-500 mt-1 text-center">Browse events and tap Enquire Now to connect with organizers.</p>
          <button onClick={() => navigate('/')} className="mt-5 bg-brand-magenta hover:bg-pink-700 text-white px-6 py-3 rounded-2xl font-extrabold text-sm transition-colors">
            Browse Events
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3 pb-8">
          {items.map((item) => {
            const s = STATUS_STYLES[item.status] || STATUS_STYLES.pending;
            return (
              <div key={item.id} data-testid={`enquiry-${item.id}`} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <button
                  onClick={() => navigate(`/event/${item.event_id}`)}
                  className="flex items-center gap-3 p-3 w-full text-left hover:bg-gray-50 transition-colors"
                >
                  <img src={item.event_snapshot.banner_url} alt="" className="w-[72px] h-[72px] rounded-xl object-cover bg-gray-100 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-extrabold text-gray-900 line-clamp-2 leading-snug">{item.event_snapshot.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{item.event_snapshot.city} · {dayjs(item.event_snapshot.start_datetime).format('MMM D')}</p>
                    <p className="text-[11px] text-gray-400">By {item.event_snapshot.organizer_name}</p>
                  </div>
                </button>
                <div className="flex items-center gap-2 px-3 py-2.5 border-t border-gray-100">
                  <span className={`${s.bg} ${s.text} text-[10px] font-black tracking-wide px-2.5 py-1 rounded-full`}>
                    {s.label}
                  </span>
                  <span className="text-[11px] text-gray-500 flex-1">
                    {item.guests_count} guest{item.guests_count > 1 ? 's' : ''} · {dayjs(item.created_at).format('MMM D, h:mm A')}
                  </span>
                  <a
                    data-testid={`enquiry-call-${item.id}`}
                    href={`tel:${item.organizer_phone}`}
                    className="flex items-center gap-1 bg-brand-magenta hover:bg-pink-700 text-white text-xs font-black px-3 py-1.5 rounded-full transition-colors"
                  >
                    <Phone className="w-3 h-3" /> Call
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
